/**
 * ===================================================================
 * APP SHELL — rt-* Sidebar layout
 * ===================================================================
 * Jediný layout pro celou hlavní aplikaci (admin má vlastní AdminLayout).
 * Vychází z prototype/src/shell.jsx — Sidebar (desktop 232/64px),
 * Topbar (sticky, search/device/lang/theme/bell/user), MobileTopbar + Drawer,
 * BottomNav (< lg).
 *
 * Zachovává všechny integrace z původního TopNavLayout:
 * GlobalAlertListener, BetaUpdateBanner, ActuatorsModal, EmergencyStopButton,
 * CommandPalette (⌘K), Breadcrumbs, QuickActionsFAB, HeaderWeatherWidget,
 * useUIMode (beginner/normal/advanced filtrování) a useGnnDiscovery.
 */

import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Bell,
  BookOpen,
  Calendar,
  ChevronDown,
  Layers,
  LayoutDashboard,
  LogOut,
  Menu as MenuIcon,
  Moon,
  Search,
  Settings,
  ShieldAlert,
  Sun,
  User,
  Workflow,
  Wrench,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';

import { useAlerts } from '../../hooks/useAlerts';
import { useAuth } from '../../hooks/auth';
import { useThemeContext } from '../../hooks/theme';
import { useActuators, useActiveDevice } from '../../hooks/useActuators';
import { useGnnDiscovery } from '../../hooks/useGnnDiscovery';
import { useUIMode } from '../../hooks/useIsAdvanced';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { changeLanguage } from '../../i18n';
import { Alert } from '../../types/alert';

import { ActuatorsModal } from '../Dashboard/ActuatorsModal';
import { EmergencyStopButton } from '../Controls/EmergencyStopButton';
import { Breadcrumbs } from '../Navigation/Breadcrumbs';
import { CommandPalette } from '../Navigation/CommandPalette';
import { QuickActionsFAB } from '../Navigation/QuickActionsFAB';
import { HeaderWeatherWidget } from '../UI/HeaderWeatherWidget';
import { Segmented } from '../UI/rt';
import { BetaUpdateBanner } from '../OTA/BetaUpdateBanner';
import { GlobalAlertListener } from './GlobalAlertListener';
import { DevicePicker } from './DevicePicker';
import { InterlockPanel } from '../Interlock/InterlockPanel';

interface AppShellProps {
  children: React.ReactNode;
}

type NavItem = {
  key: string;
  nameKey: string;
  path: string;
  icon: LucideIcon;
  submenu?: Array<{ nameKey: string; path: string; advanced?: boolean }>;
};

const NAV_ITEMS: NavItem[] = [
  {
    key: 'dashboard',
    nameKey: 'nav.dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    key: 'garden',
    nameKey: 'nav.myGarden',
    path: '/my-garden',
    icon: Layers,
    submenu: [
      { nameKey: 'nav.myGarden', path: '/my-garden' },
      { nameKey: 'nav.growingAreas', path: '/growing-areas' },
      { nameKey: 'nav.reservoirs', path: '/reservoirs' },
      { nameKey: 'nav.batches', path: '/growth/batches' },
      { nameKey: 'nav.journal', path: '/growth/journal' },
    ],
  },
  {
    key: 'service',
    nameKey: 'nav.service',
    path: '/servis',
    icon: Wrench,
  },
  {
    key: 'controls',
    nameKey: 'nav.controls',
    path: '/controls',
    icon: Zap,
    submenu: [
      { nameKey: 'nav.controls', path: '/controls' },
      { nameKey: 'nav.sensors', path: '/sensors' },
      { nameKey: 'nav.dosing', path: '/dosing' },
      { nameKey: 'nav.interlocks', path: '/interlocks' },
      { nameKey: 'nav.analytics', path: '/analytics' },
      { nameKey: 'nav.notifications', path: '/notifications' },
    ],
  },
  {
    key: 'automation',
    nameKey: 'nav.automation',
    path: '/automation-hub',
    icon: Workflow,
    submenu: [
      { nameKey: 'nav.rules', path: '/automation-hub?tab=rules' },
      { nameKey: 'nav.safety', path: '/automation-hub?tab=safety' },
      { nameKey: 'nav.scheduler', path: '/automation-hub?tab=scheduler' },
      { nameKey: 'nav.violations', path: '/safety/violations' },
      { nameKey: 'nav.analytics', path: '/safety/analytics', advanced: true },
    ],
  },
  {
    key: 'planning',
    nameKey: 'nav.planning',
    path: '/crop-plans',
    icon: Calendar,
    submenu: [
      { nameKey: 'nav.calendar', path: '/calendar' },
      { nameKey: 'nav.cropPlans', path: '/crop-plans' },
      { nameKey: 'nav.tasks', path: '/tasks' },
    ],
  },
  {
    key: 'library',
    nameKey: 'nav.library',
    path: '/plant-o-pedia',
    icon: BookOpen,
    submenu: [
      { nameKey: 'nav.plantopedia', path: '/plant-o-pedia' },
      { nameKey: 'nav.recipes', path: '/growth/recipes', advanced: true },
      { nameKey: 'nav.help', path: '/help' },
    ],
  },
  {
    key: 'settings',
    nameKey: 'nav.settings',
    path: '/settings',
    icon: Settings,
  },
];

const BOTTOM_NAV: Array<{ key: string; nameKey: string; path: string; icon: NavItem['icon'] }> = [
  { key: 'dashboard', nameKey: 'nav.dashboard', path: '/dashboard', icon: LayoutDashboard },
  { key: 'garden', nameKey: 'nav.myGarden', path: '/my-garden', icon: Layers },
  { key: 'automation', nameKey: 'nav.automation', path: '/automation-hub', icon: Workflow },
  { key: 'library', nameKey: 'nav.library', path: '/plant-o-pedia', icon: BookOpen },
  { key: 'settings', nameKey: 'nav.settings', path: '/settings', icon: Settings },
];

const SIDEBAR_COLLAPSED_KEY = 'rt-sidebar-collapsed';

const RtLogo: React.FC<{ size?: number }> = ({ size = 30 }) => (
  <div
    className="relative flex items-center justify-center shrink-0"
    style={{ width: size, height: size }}
  >
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className="text-[var(--rt-brand-500)]"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="30" height="30" rx="8" fill="currentColor" opacity="0.12" />
      <path d="M16 5 C10 12, 10 20, 16 27 C22 20, 22 12, 16 5 Z" fill="currentColor" />
      <path
        d="M16 27 L16 14"
        stroke="#fff"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.9"
      />
      <path
        d="M12 17 L16 20 M20 17 L16 20"
        stroke="#fff"
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.9"
      />
    </svg>
  </div>
);

const isSectionActive = (item: NavItem, pathname: string) => {
  if (pathname === item.path) return true;
  if (pathname.startsWith(item.path + '/')) return true;
  if (item.submenu) {
    return item.submenu.some((s) => {
      const base = s.path.split('?')[0];
      return pathname === base || pathname.startsWith(base + '/');
    });
  }
  return false;
};

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleColorMode, mode } = useThemeContext();
  const { user, logout } = useAuth();
  const { isBeginner, isAdvanced } = useUIMode();
  const { preferences, updatePreferences } = useUserPreferences();
  const { data: alertsData } = useAlerts();
  const { activeDeviceId, devices } = useActiveDevice();
  const { activeActuators, emergencyStop, resetEmergency, isEmergencyStopped } = useActuators({
    deviceId: activeDeviceId || '',
    autoRefresh: true,
    refreshInterval: 5000,
  });

  useGnnDiscovery({ enabled: isAdvanced, intervalMs: 5 * 60 * 1000 });

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const fromLs = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (fromLs !== null) return fromLs === 'true';
    return preferences?.sidebar_collapsed ?? false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [actuatorsOpen, setActuatorsOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    if (preferences && preferences.sidebar_collapsed !== collapsed) {
      updatePreferences({ sidebar_collapsed: collapsed });
    }
  }, [collapsed, preferences, updatePreferences]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filteredNavItems = useMemo(() => {
    if (isBeginner) {
      return [
        { key: 'dashboard', nameKey: 'nav.dashboard', path: '/dashboard', icon: LayoutDashboard },
        { key: 'garden', nameKey: 'nav.growingAreas', path: '/growing-areas', icon: Layers },
        { key: 'library', nameKey: 'nav.journal', path: '/growth/journal', icon: BookOpen },
        { key: 'settings', nameKey: 'nav.settings', path: '/settings', icon: Settings },
      ] as NavItem[];
    }
    if (isAdvanced) return NAV_ITEMS;
    return NAV_ITEMS.map((item) => {
      if (!item.submenu) return item;
      const filtered = item.submenu.filter((s) => !s.advanced);
      return { ...item, submenu: filtered.length > 0 ? filtered : undefined };
    });
  }, [isBeginner, isAdvanced]);

  const activeAlertsCount = useMemo(() => {
    if (!alertsData?.data) return 0;
    return alertsData.data.filter((a: Alert) => !a.acknowledged_at).length;
  }, [alertsData]);

  const isAdmin = user?.role === 'admin' || user?.role === 'god' || user?.role === 'technician';

  const activeDevice = devices?.find(
    (d) => String(d.id || d.device_id) === activeDeviceId,
  ) as any;
  const activeDeviceFirmware = activeDevice?.firmware_version || '';
  const isDeviceOwner =
    activeDevice &&
    user &&
    (String((activeDevice as any).user_id ?? '') === String(user.id ?? '') ||
      user.role === 'admin' ||
      user.role === 'god');

  const handleLanguageChange = (value: 'cs' | 'en') => {
    changeLanguage(value);
    updatePreferences({ language: value });
  };

  return (
    <div className="relative min-h-screen bg-[var(--rt-bg)] text-[var(--rt-ink-900)] dark:text-zinc-100">
      <GlobalAlertListener />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[var(--rt-brand-600)] focus:text-white focus:rounded-lg focus:shadow-xl focus:outline-none"
      >
        {t('actions.skipToContent')}
      </a>

      {isDeviceOwner && activeDeviceFirmware && (
        <BetaUpdateBanner
          deviceId={activeDeviceId || activeDevice?.id || ''}
          currentVersion={activeDeviceFirmware}
        />
      )}

      <div className="flex min-h-screen">
        {/* Desktop Sidebar */}
        <aside
          className={clsx(
            'hidden lg:flex flex-col shrink-0 border-r rt-divider bg-white/60 dark:bg-[#0f1412]/50 backdrop-blur rt-sidebar',
            collapsed ? 'w-[64px]' : 'w-[232px]',
          )}
        >
          <div
            className={clsx(
              'h-[var(--rt-topbar-h)] flex items-center border-b rt-divider',
              collapsed ? 'justify-center' : 'px-4 gap-2.5',
            )}
          >
            <RtLogo size={30} />
            {!collapsed && (
              <div className="leading-none">
                <div className="font-semibold text-[15px] rt-display tracking-tight">
                  Rootify
                </div>
                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[var(--rt-brand-600)] dark:text-[var(--rt-brand-300)] mt-1">
                  Hydroponics
                </div>
              </div>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto p-2 space-y-0.5" aria-label={t('nav.main')}>
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const on = isSectionActive(item, location.pathname);
              return (
                <div key={item.key}>
                  <NavLink
                    to={item.path}
                    className={clsx(
                      'w-full flex items-center gap-3 rt-h-row px-3 rounded-[10px] text-[13.5px] text-left transition-colors rt-focus-visible',
                      on
                        ? 'bg-[var(--rt-brand-50)] text-[var(--rt-brand-700)] font-medium dark:bg-[var(--rt-brand-500)]/15 dark:text-[var(--rt-brand-200)]'
                        : 'text-[var(--rt-ink-600)] dark:text-zinc-400 hover:bg-black/[.04] dark:hover:bg-white/5',
                      collapsed && 'justify-center px-0',
                    )}
                    title={collapsed ? t(item.nameKey) : undefined}
                  >
                    <Icon size={18} />
                    {!collapsed && <span>{t(item.nameKey)}</span>}
                  </NavLink>

                  {!collapsed && item.submenu && on && (
                    <div className="ml-9 my-0.5 space-y-0.5 border-l rt-divider pl-2">
                      {item.submenu.map((sub) => (
                        <NavLink
                          key={sub.path}
                          to={sub.path}
                          className={({ isActive }) =>
                            clsx(
                              'block w-full text-left px-2.5 rt-h-btn-sm rounded-md text-[12.5px]',
                              isActive
                                ? 'text-[var(--rt-brand-700)] dark:text-[var(--rt-brand-200)] font-medium'
                                : 'text-[var(--rt-ink-500)] dark:text-zinc-400 hover:bg-black/[.03] dark:hover:bg-white/5',
                            )
                          }
                        >
                          {t(sub.nameKey)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div
            className={clsx(
              'border-t rt-divider p-3 flex items-center gap-2',
              collapsed ? 'justify-center' : '',
            )}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--rt-brand-400)] to-[var(--rt-brand-700)] text-white flex items-center justify-center text-[11px] font-semibold">
              {user?.username?.charAt(0) || 'U'}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium truncate">{user?.username}</div>
                <div className="text-[10.5px] text-[var(--rt-ink-500)] dark:text-zinc-500 truncate">
                  {user?.email}
                </div>
              </div>
            )}
            {!collapsed && (
              <button
                type="button"
                onClick={() => logout()}
                aria-label={t('nav.logout')}
                title={t('nav.logout')}
                className="p-1.5 rounded-md text-[var(--rt-ink-500)] hover:text-red-500 dark:text-zinc-400 dark:hover:text-red-300 transition-colors"
              >
                <LogOut size={15} />
              </button>
            )}
          </div>
        </aside>

        {/* Main Column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Desktop Topbar */}
          <header className="sticky top-0 z-30 h-[var(--rt-topbar-h)] hidden lg:flex items-center gap-3 px-4 sm:px-6 border-b rt-divider bg-white/70 dark:bg-[#0f1412]/70 backdrop-blur-xl">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              aria-label={collapsed ? 'Rozbalit postranní panel' : 'Sbalit postranní panel'}
              title={collapsed ? 'Rozbalit postranní panel' : 'Sbalit postranní panel'}
              className="inline-flex p-2 rounded-lg hover:bg-black/[.04] dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              <MenuIcon size={16} />
            </button>

            <button
              type="button"
              onClick={() => setCommandOpen(true)}
              className="hidden md:flex items-center gap-2 h-9 px-3 rounded-[10px] bg-[var(--rt-ink-100)]/60 dark:bg-white/5 text-[13px] text-[var(--rt-ink-500)] dark:text-zinc-400 w-80 max-w-[40%] hover:bg-[var(--rt-ink-100)] dark:hover:bg-white/10"
            >
              <Search size={15} />
              <span>{t('actions.search')}…</span>
              <div className="flex-1" />
              <kbd className="font-mono text-[10px] bg-white dark:bg-black/30 border border-black/5 dark:border-white/10 px-1.5 py-0.5 rounded">
                ⌘K
              </kbd>
            </button>

            <div className="flex-1" />

            <HeaderWeatherWidget />

            <DevicePicker />

            <InterlockPanel deviceId={activeDeviceId || activeDevice?.id} />

            <div className="hidden sm:block">
              <EmergencyStopButton
                onEmergencyStop={emergencyStop}
                onEmergencyReset={resetEmergency}
                isEmergencyStopped={isEmergencyStopped}
                activeCount={activeActuators.length}
                variant="icon"
              />
            </div>

            <button
              type="button"
              onClick={() => setActuatorsOpen(true)}
              aria-label={t('nav.controls')}
              title={t('nav.controls')}
              className="relative p-2 rounded-lg hover:bg-black/[.04] dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              <Zap size={18} />
              {activeActuators.length > 0 && (
                <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--rt-brand-400)] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--rt-brand-500)]" />
                </span>
              )}
            </button>

            <Segmented
              value={i18n.language === 'en' ? 'en' : 'cs'}
              onChange={(v) => handleLanguageChange(v as 'cs' | 'en')}
              options={[
                { value: 'cs', label: 'CS' },
                { value: 'en', label: 'EN' },
              ]}
            />

            <button
              type="button"
              onClick={toggleColorMode}
              aria-label={
                mode === 'dark'
                  ? t('preferences.themeLight')
                  : t('preferences.themeDark')
              }
              title={
                mode === 'dark'
                  ? t('preferences.themeLight')
                  : t('preferences.themeDark')
              }
              className="p-2 rounded-lg hover:bg-black/[.04] dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <button
              type="button"
              onClick={() => navigate('/notifications')}
              aria-label={t('nav.notifications')}
              title={t('nav.notifications')}
              className="relative p-2 rounded-lg hover:bg-black/[.04] dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              <Bell size={18} />
              {activeAlertsCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>

            <div className="relative group ml-1">
              <button
                type="button"
                aria-label={user?.username || 'User'}
                className="flex items-center gap-2 p-1 rounded-full hover:bg-black/[.04] dark:hover:bg-white/5"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--rt-brand-400)] to-[var(--rt-brand-700)] text-white flex items-center justify-center text-[11px] font-semibold">
                  {user?.username?.charAt(0) || 'U'}
                </div>
                <ChevronDown size={12} className="opacity-60 hidden md:inline" />
              </button>
              <div className="absolute top-full right-0 mt-1 w-56 rt-card overflow-hidden p-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-150 z-40">
                <div className="px-3 py-2 border-b rt-divider">
                  <div className="font-medium text-[13px] truncate">{user?.username}</div>
                  <div className="text-[11px] text-[var(--rt-ink-500)] dark:text-zinc-500 truncate">
                    {user?.email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="w-full flex items-center gap-2 px-3 h-9 rounded-md text-[13px] text-left text-[var(--rt-ink-700)] dark:text-zinc-300 hover:bg-black/[.04] dark:hover:bg-white/5"
                >
                  <User size={14} /> {t('nav.profile')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  className="w-full flex items-center gap-2 px-3 h-9 rounded-md text-[13px] text-left text-[var(--rt-ink-700)] dark:text-zinc-300 hover:bg-black/[.04] dark:hover:bg-white/5"
                >
                  <Settings size={14} /> {t('nav.settings')}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin')}
                    className="w-full flex items-center gap-2 px-3 h-9 rounded-md text-[13px] text-left font-medium text-[var(--rt-brand-700)] dark:text-[var(--rt-brand-200)] hover:bg-[var(--rt-brand-50)] dark:hover:bg-[var(--rt-brand-500)]/10"
                  >
                    <ShieldAlert size={14} /> Administrace
                  </button>
                )}
                <div className="h-px bg-[var(--rt-ink-200)] dark:bg-white/10 my-1" />
                <button
                  type="button"
                  onClick={() => logout()}
                  className="w-full flex items-center gap-2 px-3 h-9 rounded-md text-[13px] text-left text-[var(--rt-ink-500)] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                >
                  <LogOut size={14} /> {t('nav.logout')}
                </button>
              </div>
            </div>
          </header>

          {/* Mobile Topbar */}
          {/* POZOR na sirku: prvky ve flexu se defaultne NEsmrsknou pod svuj obsah
              (min-width: auto). Driv se proto na uzkem telefonu obsah pruhu nevesel
              a posledni prvek — hamburger — skoncil mimo obrazovku a stranka sla
              posouvat do stran. Proto: nazev skryty na malych displejich, vyber
              zarizeni se smi smrsknout (min-w-0) a ikony vpravo maji shrink-0,
              aby zustaly viditelne za kazdou cenu. */}
          <header className="sticky top-0 z-30 h-14 lg:hidden flex items-center gap-2 px-4 border-b rt-divider bg-white/85 dark:bg-[#0f1412]/85 backdrop-blur-xl">
            <RtLogo size={26} />
            <span className="font-semibold rt-display text-[15px] tracking-tight hidden sm:inline">Rootify</span>
            <div className="flex-1 min-w-0" />
            <DevicePicker className="min-w-0 shrink" />
            <button
              type="button"
              onClick={() => navigate('/notifications')}
              aria-label={t('nav.notifications')}
              className="relative shrink-0 p-2 rounded-lg text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              <Bell size={18} />
              {activeAlertsCount > 0 && (
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              )}
            </button>
            <button
              type="button"
              onClick={toggleColorMode}
              aria-label={
                mode === 'dark'
                  ? t('preferences.themeLight')
                  : t('preferences.themeDark')
              }
              className="shrink-0 p-2 rounded-lg text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('nav.menu', 'Menu')}
              className="shrink-0 p-2 rounded-lg text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              <MenuIcon size={20} />
            </button>
          </header>

          <Breadcrumbs />

          <main
            id="main-content"
            className="relative z-10 flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8"
          >
            {children}
          </main>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileMenuOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="relative w-full max-w-xs bg-white dark:bg-[#0f1412] shadow-2xl flex flex-col">
                <div className="flex items-center justify-between p-4 border-b rt-divider">
                  <div className="flex items-center gap-2">
                    <RtLogo size={26} />
                    <span className="font-semibold rt-display text-[15px]">Rootify</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label="Zavřít menu"
                    className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
                  >
                    <X size={18} />
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
                  {filteredNavItems.map((item) => {
                    const Icon = item.icon;
                    const on = isSectionActive(item, location.pathname);
                    return (
                      <div key={item.key}>
                        <NavLink
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={clsx(
                            'w-full flex items-center gap-3 h-11 px-3 rounded-[10px] text-[14px] text-left transition-colors',
                            on
                              ? 'bg-[var(--rt-brand-50)] text-[var(--rt-brand-700)] font-medium dark:bg-[var(--rt-brand-500)]/15 dark:text-[var(--rt-brand-200)]'
                              : 'text-[var(--rt-ink-700)] dark:text-zinc-300 hover:bg-black/5 dark:hover:bg-white/5',
                          )}
                        >
                          <Icon size={18} />
                          {t(item.nameKey)}
                        </NavLink>
                        {item.submenu && (
                          <div className="ml-9 my-1 space-y-0.5">
                            {item.submenu.map((sub) => (
                              <NavLink
                                key={sub.path}
                                to={sub.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                  clsx(
                                    'block w-full text-left h-9 px-3 rounded-md text-[13px]',
                                    isActive
                                      ? 'text-[var(--rt-brand-700)] dark:text-[var(--rt-brand-300)] font-medium'
                                      : 'text-[var(--rt-ink-500)] dark:text-zinc-400 hover:bg-black/[.03] dark:hover:bg-white/5',
                                  )
                                }
                              >
                                {t(sub.nameKey)}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </nav>

                <div className="border-t rt-divider p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--rt-brand-400)] to-[var(--rt-brand-700)] text-white flex items-center justify-center text-[12px] font-semibold">
                    {user?.username?.charAt(0) || 'U'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium truncate">{user?.username}</div>
                    <div className="text-[11px] text-[var(--rt-ink-500)] dark:text-zinc-500 truncate">
                      {user?.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => logout()}
                    aria-label={t('nav.logout')}
                    className="p-2 rounded-md text-[var(--rt-ink-500)] hover:text-red-500 dark:text-zinc-400"
                  >
                    <LogOut size={16} />
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Mobile Bottom Nav */}
      <nav
        aria-label="Rychlá navigace"
        className="rt-bottomnav fixed bottom-0 inset-x-0 z-40 h-16 lg:hidden flex items-stretch border-t rt-divider"
      >
        {BOTTOM_NAV.map((item) => {
          const Icon = item.icon;
          const on = isSectionActive(item as NavItem, location.pathname);
          return (
            <NavLink
              key={item.key}
              to={item.path}
              className="flex-1 flex flex-col items-center justify-center gap-1 min-w-0 rt-focus-visible"
            >
              <Icon
                size={20}
                className={clsx(
                  on
                    ? 'text-[var(--rt-brand-600)] dark:text-[var(--rt-brand-300)]'
                    : 'text-[var(--rt-ink-400)] dark:text-zinc-500',
                )}
              />
              <span
                className={clsx(
                  'text-[10px] font-medium truncate max-w-full px-1',
                  on
                    ? 'text-[var(--rt-brand-700)] dark:text-[var(--rt-brand-300)]'
                    : 'text-[var(--rt-ink-500)] dark:text-zinc-400',
                )}
              >
                {t(item.nameKey)}
              </span>
            </NavLink>
          );
        })}
      </nav>

      <ActuatorsModal isOpen={actuatorsOpen} onClose={() => setActuatorsOpen(false)} />
      <CommandPalette isOpen={commandOpen} onClose={() => setCommandOpen(false)} />
      <QuickActionsFAB />
    </div>
  );
};

export default AppShell;
